/**
 * Asset Standardization · Data Ops · Feb 2026
 *
 * Runs Claude Sonnet 4.5 over every asset in the CRM, normalising
 * messy free-text asset_name into structured fields (asset_name /
 * MoA / indication / TA / stage / notes). Confidence-gated auto-apply.
 *
 * Per user directive: no per-row review — high + medium confidence
 * push straight to master; portfolio placeholders offer a bulk-delete.
 *
 * Route: /data-ops/asset-standardization
 */

import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Loader2, PlayCircle, CheckCircle2, AlertCircle, Sparkles,
  Trash2, ClipboardCheck, Download, ArrowUpDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 3000;

const STATUS_COLOR = {
  queued: 'bg-slate-100 text-slate-700 border-slate-300',
  running: 'bg-amber-50 text-amber-700 border-amber-300',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  failed: 'bg-rose-50 text-rose-700 border-rose-300',
};

export default function AssetStandardizationPage() {
  const [jobs, setJobs] = useState([]);
  const [activeJobId, setActiveJobId] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeBucket, setActiveBucket] = useState(null); // 'auto_applied' | 'flagged_review' | 'portfolio_flagged' | 'unchanged' | 'errors'
  const [downloadingBucket, setDownloadingBucket] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(null); // bucket key currently downloading (aggregate)
  const [aggregate, setAggregate] = useState(null); // {auto_applied, portfolio_flagged, flagged_review, unchanged, errors, not_yet_scanned, total_universe}
  const [aggregateBucket, setAggregateBucket] = useState(null); // which universe tile is expanded
  const [aggregateSamples, setAggregateSamples] = useState({}); // {bucketKey: [rows]}
  const [loadingAggregateSamples, setLoadingAggregateSamples] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const pollRef = useRef(null);

  const loadAggregate = async () => {
    try {
      const r = await axios.get(`${API}/api/data-ops/asset-standardization/aggregate`, { withCredentials: true });
      setAggregate(r.data);
    } catch (e) { /* silent */ }
  };

  const inspectAggregateBucket = async (bucketKey) => {
    // Toggle off if same tile clicked twice.
    if (aggregateBucket === bucketKey) { setAggregateBucket(null); return; }
    setAggregateBucket(bucketKey);
    if (aggregateSamples[bucketKey]) return; // cached
    setLoadingAggregateSamples(bucketKey);
    try {
      const r = await axios.get(
        `${API}/api/data-ops/asset-standardization/aggregate/inspect?bucket=${bucketKey}&limit=50`,
        { withCredentials: true },
      );
      setAggregateSamples((s) => ({ ...s, [bucketKey]: r.data.samples || [] }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to load records');
    } finally {
      setLoadingAggregateSamples(null);
    }
  };

  const loadJobs = async () => {
    try {
      const r = await axios.get(`${API}/api/data-ops/asset-standardization/jobs`, { withCredentials: true });
      setJobs(r.data.jobs || []);
      if (!activeJobId && (r.data.jobs || [])[0]) setActiveJobId(r.data.jobs[0].job_id);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { loadJobs(); loadAggregate(); loadLatestPlc(); loadPlcCounts(); loadResolvePending(); loadTrgAggregate(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeJobId) return;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/api/data-ops/asset-standardization/jobs/${activeJobId}`, { withCredentials: true });
        setActiveJob(r.data);
        if (['done', 'failed'].includes(r.data.status)) {
          if (pollRef.current) clearInterval(pollRef.current);
          loadJobs();
          loadAggregate();
        }
      } catch (err) { /* transient */ }
    };
    tick();
    pollRef.current = setInterval(tick, POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const startRun = async (dryRun) => {
    setStarting(true);
    try {
      const r = await axios.post(
        `${API}/api/data-ops/asset-standardization/run?dry_run=${dryRun}&max_assets=100000&scope=all&force_full_rescan=false`,
        {}, { withCredentials: true },
      );
      toast.success(`Standardization job started · ${r.data.job_id} (skipping already-scanned)`);
      setActiveJobId(r.data.job_id);
      await loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to start job');
    } finally {
      setStarting(false);
    }
  };

  // ── Placeholder Classifier (AI second-pass on portfolio placeholders) ──
  const [plcJob, setPlcJob] = useState(null);
  const [plcStarting, setPlcStarting] = useState(false);
  const [plcApplying, setPlcApplying] = useState(null); // 'keep' | 'exclude' | 'split'
  const [plcActiveBucket, setPlcActiveBucket] = useState(null); // 'keep' | 'split' | 'exclude' | 'error'
  const [plcCounts, setPlcCounts] = useState(null); // live { totals, eligible, unclassified, total_placeholders, threshold }
  const [plcBucketData, setPlcBucketData] = useState(null); // /items response for the active bucket
  const [plcBucketLoading, setPlcBucketLoading] = useState(false);
  const [plcSelected, setPlcSelected] = useState({}); // { asset_id: true }
  const [plcDownloading, setPlcDownloading] = useState(null); // bucket key currently exporting
  const [plcSelectedSkipped, setPlcSelectedSkipped] = useState({}); // { asset_id: true } — skipped rows the reviewer wants to force-apply
  const [plcRerunning, setPlcRerunning] = useState(false);
  const [autoAppliedAudit, setAutoAppliedAudit] = useState(null); // {outcome, total, samples: [{source_name, standardized_name, split_assets, confidence, reason}]}
  // ─── AI Resolve Remaining (universal cross-engine orchestrator) ───
  const [resolvePending, setResolvePending] = useState(null); // { unclassified_placeholders, flagged_review, low_confidence, total }
  const [resolveStarting, setResolveStarting] = useState(false);
  const [resolveResult, setResolveResult] = useState(null); // { jobs: [...], total_records }
  const [resolveStatus, setResolveStatus] = useState(null); // aggregated /status response
  const [resolveSummary, setResolveSummary] = useState(null); // final summary snapshot for the modal
  const resolvePollRef = useRef(null);
  const resolveStartRef = useRef(null); // wall-clock ms when the current run began (for stuck detection)

  const cancelResolveRemaining = async () => {
    const ids = (resolveResult?.jobs || []).map((j) => j.job_id);
    if (!ids.length) return;
    if (!window.confirm(
      `Cancel the current AI Resolve session?\n\nThis will mark the ${ids.length} job(s) as cancelled so you can retry. ` +
      `Any high-confidence records the AI already auto-applied are kept — cancelling does NOT undo work already done.`
    )) return;
    try {
      await axios.post(
        `${API}/api/data-ops/ai-resolve-remaining/cancel`,
        { job_ids: ids }, { withCredentials: true },
      );
      if (resolvePollRef.current) { clearInterval(resolvePollRef.current); resolvePollRef.current = null; }
      setResolveResult(null); setResolveStatus(null); setResolveSummary(null);
      resolveStartRef.current = null;
      toast.success('Cancelled. You can retry AI Resolve Remaining now.', { duration: 8000 });
      await Promise.all([loadResolvePending(), loadPlcCounts(), loadAggregate()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Cancel failed');
    }
  };

  const loadResolveStatus = async (jobIds) => {
    if (!jobIds || !jobIds.length) return null;
    try {
      const r = await axios.get(
        `${API}/api/data-ops/ai-resolve-remaining/status?job_ids=${jobIds.join(',')}`,
        { withCredentials: true },
      );
      setResolveStatus(r.data);
      if (r.data.all_done) {
        // Freeze the summary snapshot for the modal and stop polling.
        setResolveSummary(r.data);
        if (resolvePollRef.current) { clearInterval(resolvePollRef.current); resolvePollRef.current = null; }
        // Refresh downstream counts so tiles / dashboards reflect reality.
        loadPlcCounts(); loadAggregate(); loadResolvePending();
      }
      return r.data;
    } catch { return null; }
  };

  const loadResolvePending = async () => {
    try {
      const r = await axios.get(
        `${API}/api/data-ops/ai-resolve-remaining/pending`,
        { withCredentials: true },
      );
      setResolvePending(r.data);
    } catch { /* silent */ }
  };

  const startResolveRemaining = async () => {
    if (!resolvePending?.total) return;
    if (!window.confirm(
      `AI Resolve Remaining will process ${resolvePending.total.toLocaleString()} unresolved record(s):\n\n` +
      `  · ${resolvePending.unclassified_placeholders} unclassified portfolio placeholders\n` +
      `  · ${resolvePending.flagged_review} flagged-for-review records\n` +
      `  · ${resolvePending.low_confidence} low-confidence classifications (will be re-run)\n\n` +
      `High-confidence AI decisions are auto-applied. Everything else lands in the review queues for you.\n\n` +
      `This uses Claude tokens. Continue?`
    )) return;
    setResolveStarting(true);
    try {
      const r = await axios.post(
        `${API}/api/data-ops/ai-resolve-remaining/start`,
        {}, { withCredentials: true },
      );
      setResolveResult(r.data);
      setResolveStatus(null); setResolveSummary(null);
      resolveStartRef.current = Date.now();
      // Start unified polling across all queued jobs.
      const jobIds = (r.data.jobs || []).map((j) => j.job_id);
      if (resolvePollRef.current) clearInterval(resolvePollRef.current);
      // Prime once immediately so the card doesn't sit empty for 3s.
      loadResolveStatus(jobIds);
      resolvePollRef.current = setInterval(() => loadResolveStatus(jobIds), 3000);

      toast.success(`Kicked ${r.data.jobs.length} AI job(s) covering ${r.data.total_records.toLocaleString()} record(s). Watch the progress card above.`, { duration: 10000 });
      // Hook the placeholder job into the existing panel so its progress
      // bar / tiles refresh automatically.
      const plcQueued = r.data.jobs.find(
        (j) => j.engine === 'placeholder_classifier' || j.engine === 'placeholder_classifier_rerun',
      );
      if (plcQueued) {
        setPlcJob({ job_id: plcQueued.job_id, status: 'queued', total: plcQueued.total,
                    counters: {}, buckets: {} });
        if (plcPollRef.current) clearInterval(plcPollRef.current);
        plcPollRef.current = setInterval(() => loadPlcJob(plcQueued.job_id), 2500);
      }
      // Feb 2026 · triage engine is now driven by frontend ticks (no
      // background task, because prod pod restarts kept killing it).
      // Kick the tick loop for whichever triage job the orchestrator seeded.
      const trgQueued = r.data.jobs.find((j) => j.engine === 'flagged_review_triager');
      if (trgQueued) {
        setTrgJob({ job_id: trgQueued.job_id, status: 'running', total: trgQueued.total,
                    counters: {}, buckets: {} });
        const runTicks = async () => {
          let done = false;
          while (!done) {
            try {
              const t = await axios.post(
                `${API}/api/data-ops/flagged-review-triage/tick?job_id=${trgQueued.job_id}&limit=50`,
                {}, { withCredentials: true, timeout: 120000 },
              );
              setTrgJob(t.data);
              if (['done', 'failed', 'cancelled'].includes(t.data.status) || t.data.remaining === 0) {
                done = true;
                loadAggregate();
                break;
              }
              await new Promise((res) => setTimeout(res, 250));
            } catch (err) {
              console.warn('Triage tick failed, retrying in 5s:', err?.message || err);
              await new Promise((res) => setTimeout(res, 5000));
            }
          }
        };
        runTicks();
      }
      // Reload live counts so the button label updates.
      await Promise.all([loadResolvePending(), loadPlcCounts(), loadAggregate()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to start');
    } finally {
      setResolveStarting(false);
    }
  };
  const plcPollRef = useRef(null);

  // ── Flagged-Review Triager (AI 5-bucket split of needs_manual_review) ──
  const [trgJob, setTrgJob] = useState(null);
  // Feb 2026 · aggregate bucket counts across ALL triage runs (not just
  // the current job). The per-job counters in `trgJob` reflect one specific
  // run, but the Apply buttons operate on every asset in the DB with a
  // given `triage_bucket`, so the button labels must show the true DB-wide
  // count that will be affected. Populated from GET
  // /flagged-review-triage/aggregate-counts.
  const [trgAggregate, setTrgAggregate] = useState(null);
  const [trgStarting, setTrgStarting] = useState(false);
  const [trgApplying, setTrgApplying] = useState(null); // bucket name
  const [trgActiveBucket, setTrgActiveBucket] = useState(null);
  const trgPollRef = useRef(null);

  const loadTrgJob = async (jid) => {
    try {
      const r = await axios.get(`${API}/api/data-ops/flagged-review-triage/jobs/${jid}`, { withCredentials: true });
      setTrgJob(r.data);
      if (['done', 'failed'].includes(r.data.status)) {
        if (trgPollRef.current) clearInterval(trgPollRef.current);
        loadAggregate();
        loadTrgAggregate();
      }
    } catch { /* silent */ }
  };

  // Feb 2026 · load the DB-wide bucket totals that the Apply buttons
  // actually operate on. Runs on job completion + on demand so the
  // button labels reflect the true "will be applied" count, not just
  // the current job's slice.
  const loadTrgAggregate = async () => {
    try {
      const r = await axios.get(
        `${API}/api/data-ops/flagged-review-triage/aggregate-counts?min_confidence=0.7`,
        { withCredentials: true },
      );
      setTrgAggregate(r.data);
    } catch { /* silent */ }
  };

  const startTrgJob = async () => {
    if (!window.confirm(
      `Run AI triage on ${(aggregate?.flagged_review || 0).toLocaleString()} flagged-for-review records?\n\n` +
      `Splits them into 5 buckets: System failure (auto-retry) · Needs 2nd pass (rename) · Insufficient info · Out of scope (delete) · Human review.\n\n` +
      `NOTHING is applied automatically — review each bucket's count then click Apply per bucket.`
    )) return;
    setTrgStarting(true);
    try {
      const r = await axios.post(`${API}/api/data-ops/flagged-review-triage/run`, {}, { withCredentials: true });
      const jid = r.data.job_id;
      toast.success(`Triage started · ${jid}`);
      if (trgPollRef.current) clearInterval(trgPollRef.current);
      // Feb 2026 · SYNCHRONOUS TICK LOOP. Old flow spawned a background
      // task that never survived pod restarts on production. Now we
      // process the universe in bounded batches from the frontend — each
      // tick returns in seconds and if the pod dies mid-tick only that
      // batch's ~50 records are lost (frontend just retries).
      const runTicks = async () => {
        let doneOrCancelled = false;
        while (!doneOrCancelled) {
          try {
            const t = await axios.post(
              `${API}/api/data-ops/flagged-review-triage/tick?job_id=${jid}&limit=50`,
              {}, { withCredentials: true, timeout: 120000 },
            );
            setTrgJob(t.data);
            if (['done', 'failed', 'cancelled'].includes(t.data.status) || t.data.remaining === 0) {
              doneOrCancelled = true;
              loadAggregate();
              break;
            }
            // Tiny yield so the UI can repaint between ticks.
            await new Promise((res) => setTimeout(res, 250));
          } catch (err) {
            // Transient pod hiccup — wait 5s and retry the same tick.
            // The next call will pick up wherever we left off because
            // the tick endpoint queries only unresolved records.
            console.warn('Triage tick failed, retrying in 5s:', err?.message || err);
            await new Promise((res) => setTimeout(res, 5000));
          }
        }
      };
      runTicks();
      // Also start a light polling loop so the visible progress card
      // updates independently of the tick loop (belt & braces).
      trgPollRef.current = setInterval(() => loadTrgJob(jid), 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to start triage');
    } finally {
      setTrgStarting(false);
    }
  };

  const applyTrgBucket = async (bucket) => {
    // Use aggregate count (DB-wide) if available — that's what actually
    // gets applied. Fallback to per-job count while aggregate loads.
    const cnt = (trgAggregate?.buckets?.[bucket]?.eligible ?? trgJob?.counters?.[bucket]) || 0;
    if (!cnt) return;
    const verb = {
      system_failure: 'unmark as flagged and retry via next standardization run',
      needs_second_pass: 'rename to concise Program name and clear the review flag',
      insufficient_info: 'tag as insufficient info and remove from review queue',
      out_of_scope: 'permanently DELETE',
    }[bucket];
    if (!window.confirm(
      `Apply "${bucket}" to ${cnt.toLocaleString()} record(s) across ALL triage runs?\n\n` +
      `${bucket === 'system_failure' ? 'All records will be retried (deterministic).' : 'Only records with confidence ≥ 70% will be actioned.'} ` +
      `Committee-locked assets are preserved.\n\nAction: ${verb}.`
    )) return;
    setTrgApplying(bucket);
    try {
      const r = await axios.post(
        `${API}/api/data-ops/flagged-review-triage/apply?bucket=${bucket}&min_confidence=0.7`,
        {}, { withCredentials: true },
      );
      const summary = bucket === 'out_of_scope'
        ? `Deleted ${r.data.deleted} out-of-scope record(s).`
        : `Updated ${r.data.changed} record(s).`;
      toast.success(`${summary} Skipped ${r.data.skipped?.length || 0} (locked or low-confidence).`, { duration: 8000 });
      loadAggregate();
      loadTrgAggregate();
      if (trgJob) loadTrgJob(trgJob.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Apply failed');
    } finally {
      setTrgApplying(null);
    }
  };

  const loadPlcCounts = async () => {
    try {
      const r = await axios.get(
        `${API}/api/data-ops/placeholder-classification/bucket-counts`,
        { withCredentials: true },
      );
      setPlcCounts(r.data);
    } catch { /* silent */ }
  };

  const downloadPlcBucket = async (bucket) => {
    setPlcDownloading(bucket);
    try {
      const r = await axios.get(
        `${API}/api/data-ops/placeholder-classification/export?bucket=${bucket}`,
        { withCredentials: true, responseType: 'blob' },
      );
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `placeholder_classifier_${bucket}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${bucket} bucket sheet.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Download failed');
    } finally {
      setPlcDownloading(null);
    }
  };

  // Re-classify every row currently in a bucket using the latest AI
  // rules. Used before destructive actions so we don't delete records
  // that a refined prompt would have kept.
  const rerunBucketAI = async (bucket) => {
    if (!bucket) return;
    const total = plcBucketData?.eligible_count + plcBucketData?.skipped_count;
    if (!window.confirm(
      `Re-run AI review on ${total} record(s) in the "${bucket}" bucket?\n\n` +
      `The classifier will re-evaluate every row with the latest rules and may redistribute them into Keep / Split / Exclude. ` +
      `High-confidence decisions may be auto-applied. Low-confidence ones stay for your review.\n\n` +
      `This uses Claude tokens.`
    )) return;
    setPlcRerunning(true);
    try {
      const r = await axios.post(
        `${API}/api/data-ops/placeholder-classification/rerun-bucket?bucket=${bucket}`,
        {}, { withCredentials: true },
      );
      toast.success(`Re-running classifier on ${r.data.total} record(s). New job: ${r.data.job_id}. Watch the progress bar above.`, { duration: 10000 });
      setPlcJob({ ...r.data, counters: {}, buckets: {} });
      // Start polling.
      if (plcPollRef.current) clearInterval(plcPollRef.current);
      plcPollRef.current = setInterval(() => loadPlcJob(r.data.job_id), 2000);
      // Optimistically clear this bucket's view since data is being reset.
      setPlcSelected({});
      setPlcSelectedSkipped({});
      await Promise.all([loadPlcCounts(), loadAggregate()]);
      if (plcActiveBucket) await loadPlcBucket(plcActiveBucket);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Re-run failed');
    } finally {
      setPlcRerunning(false);
    }
  };

  const loadPlcBucket = async (bucket) => {
    if (!bucket) return;
    setPlcBucketLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/data-ops/placeholder-classification/items?bucket=${bucket}&limit=500`,
        { withCredentials: true },
      );
      setPlcBucketData(r.data);
      // Auto-select every eligible row so the reviewer's default action
      // is "apply everything Claude suggested" — they can deselect any
      // they want to skip.
      const eligible = r.data?.eligible || [];
      const sel = {};
      eligible.forEach((row) => { sel[row.asset_id] = true; });
      setPlcSelected(sel);
      setPlcSelectedSkipped({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to load bucket items');
      setPlcBucketData(null);
    } finally {
      setPlcBucketLoading(false);
    }
  };

  const selectPlcBucket = async (bucket) => {
    // Toggle off if same tab clicked.
    if (plcActiveBucket === bucket) {
      setPlcActiveBucket(null);
      setPlcBucketData(null);
      setPlcSelected({});
      setPlcSelectedSkipped({});
      return;
    }
    setPlcActiveBucket(bucket);
    setPlcBucketData(null);
    setPlcSelected({});
    setPlcSelectedSkipped({});
    await loadPlcBucket(bucket);
  };

  const togglePlcRow = (aid) => {
    setPlcSelected((s) => ({ ...s, [aid]: !s[aid] }));
  };
  const togglePlcAll = () => {
    const eligible = plcBucketData?.eligible || [];
    const allSelected = eligible.length > 0 && eligible.every((r) => plcSelected[r.asset_id]);
    if (allSelected) setPlcSelected({});
    else {
      const s = {};
      eligible.forEach((r) => { s[r.asset_id] = true; });
      setPlcSelected(s);
    }
  };

  // ── Force-apply: select skipped rows the reviewer wants to override
  //    on (only below-confidence rows are forceable; locked / structural
  //    blockers are not selectable in the UI). ──
  const togglePlcSkippedRow = (aid) => {
    setPlcSelectedSkipped((s) => ({ ...s, [aid]: !s[aid] }));
  };
  const togglePlcSkippedAll = () => {
    const forceable = (plcBucketData?.skipped || []).filter(
      (r) => r.skip_reason && r.skip_reason.startsWith('Confidence '),
    );
    const anyChecked = Object.values(plcSelectedSkipped).some(Boolean);
    if (anyChecked) setPlcSelectedSkipped({});
    else {
      const s = {};
      forceable.forEach((r) => { s[r.asset_id] = true; });
      setPlcSelectedSkipped(s);
    }
  };
  const forceApplyPlcSkipped = async (outcome) => {
    const ids = Object.keys(plcSelectedSkipped).filter((k) => plcSelectedSkipped[k]);
    if (!ids.length) return;
    const verb = outcome === 'keep' ? 'rename + keep as Program'
      : outcome === 'exclude' ? 'permanently DELETE'
      : 'split into new assets + delete original';
    if (!window.confirm(
      `Force-apply "${outcome.toUpperCase()}" to ${ids.length} below-confidence record(s)?\n\n` +
      `You are overriding the 70% confidence gate. Only do this after reviewing the AI's proposal. ` +
      `Committee-locked assets are still preserved.\n\nAction: ${verb}.`
    )) return;
    setPlcApplying(outcome);
    try {
      const r = await axios.post(
        `${API}/api/data-ops/placeholder-classification/apply?outcome=${outcome}&min_confidence=0.0`,
        { asset_ids: ids }, { withCredentials: true },
      );
      const applied = outcome === 'keep' ? r.data.changed
        : outcome === 'exclude' ? r.data.deleted
        : r.data.deleted;
      const detail = outcome === 'keep'
        ? `Force-renamed ${r.data.changed} placeholder(s) into Programs.`
        : outcome === 'exclude'
        ? `Force-deleted ${r.data.deleted} platform record(s).`
        : `Force-split ${r.data.deleted} record(s) into ${r.data.created} new assets.`;
      if (applied === 0) {
        toast.warning(`${detail} Skipped ${r.data.skipped?.length || 0}.`, { duration: 9000 });
      } else {
        toast.success(`${detail} Bucket count will drop by ${applied}.`, { duration: 8000 });
      }
      setPlcSelectedSkipped({});
      await Promise.all([loadAggregate(), loadPlcCounts()]);
      if (plcJob) loadPlcJob(plcJob.job_id);
      if (plcActiveBucket) await loadPlcBucket(plcActiveBucket);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Force apply failed');
    } finally {
      setPlcApplying(null);
    }
  };

  // ── Reject / Move-to-Manual-Review / Delete ──
  // Runs the new /reject endpoint against the currently-selected asset
  // ids (either eligible or skipped panel — caller passes which set).
  const rejectPlcRows = async (action, ids) => {
    if (!ids || !ids.length) return;
    const verb = action === 'delete'
      ? `permanently DELETE ${ids.length} record(s) (and their engagement links)`
      : `move ${ids.length} record(s) back to Manual Review (clears the AI classification)`;
    if (!window.confirm(
      `${action === 'delete' ? '⚠️  ' : ''}Confirm: ${verb}?\n\nCommittee-locked assets are always preserved.`,
    )) return;
    setPlcApplying('reject');
    try {
      const r = await axios.post(
        `${API}/api/data-ops/placeholder-classification/reject`,
        { asset_ids: ids, action }, { withCredentials: true },
      );
      const moved = r.data.moved_to_manual_review || 0;
      const del = r.data.deleted || 0;
      const skipped = r.data.skipped?.length || 0;
      if (action === 'delete') {
        toast.success(`Deleted ${del} record(s). Skipped ${skipped} (committee-locked).`, { duration: 8000 });
      } else {
        toast.success(`Moved ${moved} record(s) to Manual Review. Skipped ${skipped} (committee-locked).`, { duration: 8000 });
      }
      setPlcSelected({});
      setPlcSelectedSkipped({});
      await Promise.all([loadAggregate(), loadPlcCounts()]);
      if (plcJob) loadPlcJob(plcJob.job_id);
      if (plcActiveBucket) await loadPlcBucket(plcActiveBucket);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Reject failed');
    } finally {
      setPlcApplying(null);
    }
  };

  const loadPlcJob = async (jid) => {
    try {
      const r = await axios.get(`${API}/api/data-ops/placeholder-classification/jobs/${jid}`, { withCredentials: true });
      setPlcJob(r.data);
      if (['done', 'failed'].includes(r.data.status)) {
        if (plcPollRef.current) clearInterval(plcPollRef.current);
        loadAggregate();
        loadPlcCounts();
      }
    } catch { /* silent */ }
  };

  // On page load: auto-restore the latest classifier job (running or
  // completed). Prevents users from thinking the panel disappeared and
  // accidentally starting a new job (=> wasted Claude tokens).
  const loadLatestPlc = async () => {
    try {
      const r = await axios.get(`${API}/api/data-ops/placeholder-classification/latest`, { withCredentials: true });
      if (r.data && r.data.job_id) {
        setPlcJob(r.data);
        if (r.data.status === 'running') {
          if (plcPollRef.current) clearInterval(plcPollRef.current);
          plcPollRef.current = setInterval(() => loadPlcJob(r.data.job_id), 4000);
        }
      }
    } catch { /* silent */ }
  };

  const cancelPlcJob = async () => {
    if (!plcJob || !['queued', 'running'].includes(plcJob.status)) return;
    if (!window.confirm(
      `Cancel classifier job ${plcJob.job_id}?\n\n` +
      `The classifications ALREADY written to assets will be preserved — you can still Apply them once cancelled. This just stops any further Claude calls.`
    )) return;
    try {
      await axios.post(
        `${API}/api/data-ops/placeholder-classification/jobs/${plcJob.job_id}/cancel`,
        {}, { withCredentials: true },
      );
      toast.success('Classifier cancelled. Token bleed stopped.');
      if (plcPollRef.current) clearInterval(plcPollRef.current);
      loadPlcJob(plcJob.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Cancel failed');
    }
  };

  const startPlcJob = async () => {
    if (!window.confirm(
      `Run AI classifier on ${(aggregate?.portfolio_flagged || 0).toLocaleString()} portfolio placeholders?\n\n` +
      `Claude Sonnet 4.5 will suggest one of KEEP (rename to Program) / SPLIT (into individual assets) / EXCLUDE (platform/tech).\n\n` +
      `NOTHING is applied automatically — you review the tiles and click Apply per outcome.`
    )) return;
    setPlcStarting(true);
    try {
      const r = await axios.post(`${API}/api/data-ops/placeholder-classification/run`, {}, { withCredentials: true });
      toast.success(`Classifier started · ${r.data.job_id}`);
      if (plcPollRef.current) clearInterval(plcPollRef.current);
      plcPollRef.current = setInterval(() => loadPlcJob(r.data.job_id), 4000);
      loadPlcJob(r.data.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to start classifier');
    } finally {
      setPlcStarting(false);
    }
  };

  const applyPlcOutcome = async (outcome) => {
    const selectedIds = Object.keys(plcSelected).filter((k) => plcSelected[k]);
    // If reviewer hasn't loaded the bucket, fall back to counter-based confirm.
    const cnt = selectedIds.length || (plcCounts?.eligible?.[outcome] || 0);
    if (!cnt) {
      toast.info('Nothing selected. Pick a tab, review the rows, then tick the ones you want to apply.');
      return;
    }
    const verb = outcome === 'keep' ? 'rename + keep as Program'
      : outcome === 'exclude' ? 'permanently DELETE'
      : 'split into new assets + delete original';
    if (!window.confirm(
      `Apply "${outcome.toUpperCase()}" to ${cnt} selected record(s)?\n\n` +
      `Committee-locked assets are always preserved.\n\nAction: ${verb}.`
    )) return;
    setPlcApplying(outcome);
    try {
      const body = selectedIds.length ? { asset_ids: selectedIds } : {};
      const r = await axios.post(
        `${API}/api/data-ops/placeholder-classification/apply?outcome=${outcome}&min_confidence=0.7`,
        body, { withCredentials: true },
      );
      const applied = outcome === 'keep' ? r.data.changed
        : outcome === 'exclude' ? r.data.deleted
        : r.data.deleted;
      const detail = outcome === 'keep'
        ? `Renamed ${r.data.changed} placeholder(s) into Programs.`
        : outcome === 'exclude'
        ? `Deleted ${r.data.deleted} platform record(s).`
        : `Split ${r.data.deleted} record(s) into ${r.data.created} new assets.`;
      if (applied === 0) {
        toast.warning(
          `0 records processed. ${detail} Skipped ${r.data.skipped?.length || 0}. ` +
          `Rows may already be processed or now committee-locked — the tab below has been refreshed.`,
          { duration: 9000 },
        );
      } else {
        toast.success(`${detail} Skipped ${r.data.skipped?.length || 0} (locked).`, { duration: 8000 });
      }
      // Refresh live state so counts/buttons reflect the new DB truth.
      await Promise.all([loadAggregate(), loadPlcCounts()]);
      if (plcJob) loadPlcJob(plcJob.job_id);
      if (plcActiveBucket) await loadPlcBucket(plcActiveBucket);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Apply failed');
    } finally {
      setPlcApplying(null);
    }
  };


  const downloadBucket = async (bucketKey) => {
    if (!activeJobId) return;
    setDownloadingBucket(bucketKey);
    try {
      const r = await axios.get(
        `${API}/api/data-ops/asset-standardization/jobs/${activeJobId}/export?bucket=${bucketKey}`,
        { withCredentials: true, responseType: 'blob' },
      );
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `standardization_${activeJobId}_${bucketKey}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${bucketKey} sheet.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Download failed');
    } finally {
      setDownloadingBucket(null);
    }
  };

  const downloadAggregate = async (bucketKey) => {
    setDownloadingAll(bucketKey);
    try {
      const r = await axios.get(
        `${API}/api/data-ops/asset-standardization/export-all?bucket=${bucketKey}`,
        { withCredentials: true, responseType: 'blob' },
      );
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `standardization_ALL_${bucketKey}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded all-time ${bucketKey} sheet.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Download failed');
    } finally {
      setDownloadingAll(null);
    }
  };

  const applyDryRun = async () => {
    if (!activeJob || !activeJob.dry_run) return;
    if (!window.confirm(`Apply ${activeJob.counters?.auto_applied || 0} pending high+medium confidence changes to the master assets table? Cascades to Committee Review, Reviewer, Client Portal.`)) return;
    try {
      const r = await axios.post(
        `${API}/api/data-ops/asset-standardization/jobs/${activeJobId}/apply`,
        {}, { withCredentials: true },
      );
      toast.success(`Applied ${r.data.applied} standardizations.`);
      const j = await axios.get(`${API}/api/data-ops/asset-standardization/jobs/${activeJobId}`, { withCredentials: true });
      setActiveJob(j.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Apply failed');
    }
  };

  const bulkDeletePortfolios = async () => {
    // Universe-level bulk delete: operates on ALL assets flagged
    // `is_portfolio_placeholder=true` across every prior run — not just
    // the latest job's bucket. Uses the aggregate count for confirmation.
    const n = aggregate?.portfolio_flagged || 0;
    if (!n) {
      toast.info('No portfolio placeholders to delete.');
      return;
    }
    if (!window.confirm(
      `Delete ${n.toLocaleString()} portfolio placeholder asset(s) permanently?\n\n` +
      `These are records like "Global oncology product" / "Three marketed products" that aren't real assets. ` +
      `Their engagement_asset rows will also be cleaned up. Assets that are Under Review / Approved / Rejected on Viatris will be preserved.`
    )) return;
    setDeleting(true);
    try {
      const r = await axios.post(
        `${API}/api/data-ops/asset-standardization/portfolio-placeholders/bulk-delete-all`,
        {}, { withCredentials: true },
      );
      toast.success(
        `Deleted ${(r.data.deleted_assets || 0).toLocaleString()} portfolio placeholder(s). ` +
        `Also removed ${r.data.deleted_engagement_rows || 0} engagement rows. ` +
        `Skipped ${r.data.skipped?.length || 0} (locked by committee).`,
        { duration: 8000 },
      );
      // Refresh the aggregate + active job.
      loadAggregate();
      if (activeJobId) {
        try {
          const j = await axios.get(`${API}/api/data-ops/asset-standardization/jobs/${activeJobId}`, { withCredentials: true });
          setActiveJob(j.data);
        } catch { /* noop */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Bulk delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const cancelJob = async () => {
    if (!activeJob) return;
    if (!window.confirm(`Mark job ${activeJob.job_id} as failed and unblock the Start buttons? Use this if the job is stuck from an older build.`)) return;
    try {
      await axios.post(
        `${API}/api/data-ops/asset-standardization/jobs/${activeJob.job_id}/cancel`,
        {}, { withCredentials: true },
      );
      toast.success('Job cancelled. You can start a new run now.');
      const j = await axios.get(`${API}/api/data-ops/asset-standardization/jobs/${activeJobId}`, { withCredentials: true });
      setActiveJob(j.data);
      await loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Cancel failed');
    }
  };

  const counters = activeJob?.counters || {};
  const buckets = activeJob?.buckets || {};
  const scanPct = activeJob?.total ? Math.round(100 * (counters.scanned || 0) / activeJob.total) : 0;
  // Feb 2026 · A job counts as "runnable to start a new one" when either
  // (a) it's terminal (done/failed) OR (b) it's stuck: status=running/queued
  // AND either older than 30 minutes OR progress bar shows 100% but never
  // flipped to done (legacy counter-bug jobs).
  const stuckLikely = !!(
    activeJob && ['running', 'queued'].includes(activeJob.status) &&
    (
      (activeJob.total > 0 && (counters.scanned || 0) >= activeJob.total) ||
      (activeJob.started_at && (Date.now() - new Date(activeJob.started_at).getTime()) > 30 * 60 * 1000)
    )
  );
  const runnable = !activeJob || ['done', 'failed'].includes(activeJob.status) || stuckLikely;
  const alreadyDeleted = !!activeJob?.portfolio_bulk_deleted_at;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="asset-standardization-page">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
        <Sparkles className="w-3 h-3" /> AI Data Ops
      </div>
      <h1 className="text-2xl font-bold text-slate-900">Asset Standardization Engine</h1>
      <p className="text-sm text-slate-600 max-w-3xl">
        Claude Sonnet 4.5 cleans messy asset records into structured fields
        (<strong>asset_name</strong> · <strong>MoA</strong> · <strong>indication</strong> · <strong>TA</strong> · <strong>stage</strong>).
        This page shows the current standardization status of your full asset universe — deduplicated across all runs.
      </p>

      {/* ═════════════════════════════════════════════════════════════════
          UNIVERSE-LEVEL CONSOLIDATED RESULT
          ═════════════════════════════════════════════════════════════════ */}
      {aggregate && (
        <div className="rounded-xl border-2 border-violet-300 bg-gradient-to-br from-violet-50/80 to-white p-5 space-y-4" data-testid="consolidated-summary">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-violet-700 font-bold">Universe-level consolidated result</div>
              <div className="text-lg font-bold text-slate-900">Consolidated Asset Standardization Summary</div>
              <div className="text-xs text-slate-500 max-w-2xl">
                Deduplicated at the asset level across every run. Each asset appears once, in its latest final bucket.
              </div>
            </div>
          </div>

          {/* Progress bar — universe scan progress */}
          {(() => {
            const scanned = Math.max(0, (aggregate.total_universe || 0) - (aggregate.not_yet_scanned || 0));
            const pct = aggregate.total_universe ? Math.round(100 * scanned / aggregate.total_universe) : 0;
            return (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs text-slate-700">
                  <span className="font-semibold">Universe scan progress</span>
                  <span className="font-mono">
                    {scanned.toLocaleString()} / {(aggregate.total_universe || 0).toLocaleString()} assets scanned ({pct}%)
                  </span>
                </div>
                <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-violet-500 transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })()}

          {/* Seven summary tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            <Counter label="Total assets" value={aggregate.total_universe || 0} tone="slate" testid="agg-total" />
            <Counter label="Auto-applied" value={aggregate.auto_applied || 0} tone="emerald"
              active={aggregateBucket === 'auto_applied'}
              onClick={() => inspectAggregateBucket('auto_applied')}
              onDownload={() => downloadAggregate('auto_applied')} downloading={downloadingAll === 'auto_applied'}
              testid="agg-auto-applied" />
            <Counter label="Portfolio placeholders" value={aggregate.portfolio_flagged || 0} tone="rose"
              active={aggregateBucket === 'portfolio_flagged'}
              onClick={() => inspectAggregateBucket('portfolio_flagged')}
              onDownload={() => downloadAggregate('portfolio_flagged')} downloading={downloadingAll === 'portfolio_flagged'}
              testid="agg-portfolio-flagged" />
            <Counter label="Flagged for review" value={aggregate.flagged_review || 0} tone="amber"
              active={aggregateBucket === 'flagged_review'}
              onClick={() => inspectAggregateBucket('flagged_review')}
              onDownload={() => downloadAggregate('flagged_review')} downloading={downloadingAll === 'flagged_review'}
              testid="agg-flagged-review" />
            <Counter label="Unchanged" value={aggregate.unchanged || 0} tone="slate"
              active={aggregateBucket === 'unchanged'}
              onClick={() => inspectAggregateBucket('unchanged')}
              onDownload={() => downloadAggregate('unchanged')} downloading={downloadingAll === 'unchanged'}
              testid="agg-unchanged" />
            <Counter label="Errors" value={aggregate.errors || 0} tone="rose"
              active={aggregateBucket === 'errors'}
              onClick={() => inspectAggregateBucket('errors')}
              onDownload={() => downloadAggregate('errors')} downloading={downloadingAll === 'errors'}
              testid="agg-errors" />
            <Counter label="Not yet scanned" value={aggregate.not_yet_scanned || 0} tone="amber"
              active={aggregateBucket === 'not_yet_scanned'}
              onClick={() => inspectAggregateBucket('not_yet_scanned')}
              onDownload={() => downloadAggregate('not_yet_scanned')} downloading={downloadingAll === 'not_yet_scanned'}
              testid="agg-not-yet-scanned" />
          </div>
          <div className="text-[11px] text-slate-500">
            Click a tile to preview records · click the download icon to export the complete bucket to Excel.
          </div>

          {/* Inline aggregate inspector */}
          {aggregateBucket && (
            <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2" data-testid="aggregate-inspector">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-slate-700">
                  {aggregateBucket.replace('_', ' ')} · showing up to 50 samples
                </div>
                <button
                  onClick={() => setAggregateBucket(null)}
                  className="text-[11px] text-slate-500 hover:text-slate-900"
                  data-testid="close-aggregate-inspector"
                >
                  close
                </button>
              </div>
              {loadingAggregateSamples === aggregateBucket ? (
                <div className="text-xs text-slate-500 py-3 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading samples...
                </div>
              ) : (aggregateSamples[aggregateBucket] || []).length === 0 ? (
                <div className="text-xs text-slate-500 py-3">No records in this bucket.</div>
              ) : (
                <div className="max-h-80 overflow-auto text-xs divide-y divide-slate-100" data-testid="aggregate-sample-list">
                  {(aggregateSamples[aggregateBucket] || []).map((row, i) => (
                    <div key={i} className="py-1.5">
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-slate-800 truncate flex-1" title={row.asset_name || row.source_name || row.asset_id}>
                          {row.asset_name || row.source_name || row.asset_id || '—'}
                        </span>
                        {row.company_name && (
                          <span className="text-[10px] text-slate-500 truncate max-w-[30%]" title={row.company_name}>
                            {row.company_name}
                          </span>
                        )}
                      </div>
                      {row.reason && <div className="text-[11px] text-slate-500 mt-0.5" title={row.reason}>{row.reason}</div>}
                      {row.error && <div className="text-[11px] text-rose-700 mt-0.5" title={row.error}>{row.error}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════════
          FINAL ACTIONS BAR
          ═════════════════════════════════════════════════════════════════ */}
      {aggregate && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid="actions-bar">
          {/* AI Resolve Remaining — universal cross-engine one-click.
              Sits at the top so it's the reviewer's primary entry point.
              Falls back to a "Everything resolved" state when nothing
              unresolved remains. */}
          {resolvePending && (
            resolvePending.total > 0 ? (
              <div className="rounded-lg border-2 border-violet-300 bg-gradient-to-br from-violet-50 to-fuchsia-50 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="ai-resolve-remaining-panel">
                <div className="flex-1 min-w-[280px]">
                  <div className="text-[10px] uppercase tracking-widest text-violet-700 font-bold">One-click AI resolution</div>
                  <div className="text-lg font-bold text-slate-900 mt-0.5">
                    {resolvePending.total.toLocaleString()} record(s) still unresolved
                  </div>
                  <div className="text-[11px] text-slate-600 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                    {resolvePending.unclassified_placeholders > 0 && (
                      <span>· {resolvePending.unclassified_placeholders.toLocaleString()} unclassified placeholders</span>
                    )}
                    {resolvePending.flagged_review > 0 && (
                      <span>· {resolvePending.flagged_review.toLocaleString()} flagged for review</span>
                    )}
                    {resolvePending.low_confidence > 0 && (
                      <span>· {resolvePending.low_confidence.toLocaleString()} low-confidence to re-run</span>
                    )}
                  </div>
                </div>
                <Button
                  onClick={startResolveRemaining}
                  disabled={resolveStarting || (resolveStatus && !resolveStatus.all_done)}
                  size="lg"
                  className="bg-violet-600 hover:bg-violet-700 text-white shadow-md"
                  data-testid="ai-resolve-remaining-btn"
                  title="Fire every AI engine at once across all unresolved records. High-confidence decisions auto-apply."
                >
                  {resolveStarting
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <Sparkles className="w-4 h-4 mr-2" />}
                  {resolveStatus && !resolveStatus.all_done
                    ? `Resolving… ${resolveStatus.progress_pct}%`
                    : `AI Resolve Remaining (${resolvePending.total.toLocaleString()})`}
                </Button>
              </div>
            ) : (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2" data-testid="ai-resolve-complete">
                <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                <span className="text-sm font-semibold text-emerald-800">All records resolved.</span>
                <span className="text-[11px] text-emerald-700">Nothing awaiting AI review across any engine.</span>
              </div>
            )
          )}

          {/* Combined progress card — visible while any resolve job is
              still running, so the multi-engine operation feels like ONE
              unified job to the reviewer. */}
          {resolveStatus && !resolveStatus.all_done && (
            <div className="rounded-lg border border-violet-200 bg-white p-4 space-y-2" data-testid="resolve-progress-card">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-violet-700 font-bold">Resolving remaining records</div>
                  <div className="text-sm font-semibold text-slate-800 mt-0.5">
                    {(resolveStatus.total_scanned || 0).toLocaleString()} of {(resolveStatus.total_target || 0).toLocaleString()} processed
                    <span className="ml-2 text-xs text-slate-500 font-normal">({resolveStatus.progress_pct}%)</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-[11px] text-slate-500">
                    {(resolveStatus.jobs || []).map((j) => (
                      <span key={j.job_id} className="mr-3">
                        <span className="text-slate-400">{j.engine?.replace('_', ' ')}:</span>{' '}
                        <span className={j.status === 'done' ? 'text-emerald-700 font-semibold' : j.status === 'running' ? 'text-violet-700' : 'text-slate-500'}>
                          {j.status}
                        </span>
                      </span>
                    ))}
                  </div>
                  {/* Cancel — always visible so a stuck session
                      (post-redeploy pod restart kills BackgroundTasks)
                      never leaves the reviewer trapped. */}
                  <button
                    onClick={cancelResolveRemaining}
                    className="text-[11px] px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-100"
                    data-testid="resolve-cancel-btn"
                    title="Cancel this Resolve session. Auto-applied records are kept; you can start a new run."
                  >
                    Cancel
                  </button>
                </div>
              </div>
              {/* Stuck warning — surfaces when no progress after 60s
                  from start, OR when the backend detects a stale worker
                  (last_progress_at > 3 min old). The second condition
                  catches the "23% stuck" case where the engine made
                  real progress then stalled. */}
              {((resolveStartRef.current && (Date.now() - resolveStartRef.current) > 60000 && (resolveStatus.total_scanned || 0) === 0) || resolveStatus.any_stale) && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="resolve-stuck-warning">
                  ⚠ {resolveStatus.any_stale
                    ? `Worker appears dead — no progress for ${(resolveStatus.jobs?.find(j => j.is_stale)?.stale_since_seconds || 180)} seconds.`
                    : 'No progress after 60 seconds. The background worker may have been killed by a recent deploy.'}
                  {' '}Click <strong>Cancel</strong> and then <strong>AI Resolve Remaining</strong> again — already-processed records are preserved.
                </div>
              )}
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-violet-500 transition-all duration-500" style={{ width: `${resolveStatus.progress_pct || 0}%` }} />
              </div>
              {(resolveStatus.auto_applied?.total || 0) > 0 && (
                <div className="text-[11px] text-emerald-800 flex flex-wrap gap-x-3">
                  <span className="font-semibold">Auto-applied so far · {resolveStatus.auto_applied.total}</span>
                  {resolveStatus.auto_applied.keep > 0 && <span>{resolveStatus.auto_applied.keep} kept</span>}
                  {resolveStatus.auto_applied.split > 0 && <span>{resolveStatus.auto_applied.split} split</span>}
                  {resolveStatus.auto_applied.exclude > 0 && <span>{resolveStatus.auto_applied.exclude} excluded</span>}
                </div>
              )}
            </div>
          )}

          {/* Post-run summary modal — pops when every job hits `done`.
              Gives the reviewer jump-links to any bucket that still
              needs manual review, and a clean dismissal path so the
              screen doesn't stay cluttered. */}
          {resolveSummary && (
            <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" data-testid="resolve-summary-modal" onClick={() => setResolveSummary(null)}>
              <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                  </div>
                  <div className="flex-1">
                    <div className="text-lg font-bold text-slate-900">AI Resolve Complete</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Processed {resolveSummary.total_scanned.toLocaleString()} record(s) across {resolveSummary.jobs.length} engine(s).
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                    <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Auto-applied by AI</div>
                    <div className="text-3xl font-bold text-emerald-900 mt-1">{resolveSummary.auto_applied.total.toLocaleString()}</div>
                    <div className="text-[11px] text-emerald-700 mt-1">
                      {resolveSummary.auto_applied.keep} kept · {resolveSummary.auto_applied.split} split · {resolveSummary.auto_applied.exclude} excluded
                    </div>
                  </div>
                  {(() => {
                    const nr = resolveSummary.needs_review || {};
                    const humanTotal = (nr.keep || 0) + (nr.split || 0) + (nr.exclude || 0) + (nr.error || 0) + (nr.flagged_buckets || 0);
                    return (
                      <div className={`rounded-lg border p-3 ${humanTotal > 0 ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
                        <div className={`text-[10px] uppercase tracking-wide font-semibold ${humanTotal > 0 ? 'text-amber-800' : 'text-emerald-700'}`}>Need your review</div>
                        <div className={`text-3xl font-bold mt-1 ${humanTotal > 0 ? 'text-amber-900' : 'text-emerald-900'}`}>{humanTotal.toLocaleString()}</div>
                        <div className={`text-[11px] mt-1 ${humanTotal > 0 ? 'text-amber-800' : 'text-emerald-700'}`}>
                          {humanTotal === 0 ? 'Nothing pending' : `${nr.keep || 0} keep · ${nr.split || 0} split · ${nr.exclude || 0} exclude · ${nr.flagged_buckets || 0} flagged`}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Jump-links to remaining buckets */}
                {(() => {
                  const nr = resolveSummary.needs_review || {};
                  const jumps = [
                    { bucket: 'keep', count: nr.keep || 0, label: 'Keep as Program', tone: 'text-emerald-700 border-emerald-200 hover:bg-emerald-50' },
                    { bucket: 'split', count: nr.split || 0, label: 'Split into assets', tone: 'text-sky-700 border-sky-200 hover:bg-sky-50' },
                    { bucket: 'exclude', count: nr.exclude || 0, label: 'Exclude (platform)', tone: 'text-rose-700 border-rose-200 hover:bg-rose-50' },
                  ].filter((j) => j.count > 0);
                  if (jumps.length === 0) return null;
                  return (
                    <div className="pt-2 border-t border-slate-100 space-y-2">
                      <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Jump to a review queue</div>
                      <div className="flex flex-wrap gap-2">
                        {jumps.map((j) => (
                          <button
                            key={j.bucket}
                            onClick={() => { setResolveSummary(null); selectPlcBucket(j.bucket); }}
                            className={`px-3 py-1.5 text-xs font-semibold border rounded-md bg-white ${j.tone}`}
                            data-testid={`resolve-summary-jump-${j.bucket}`}
                          >
                            {j.label} ({j.count})
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                <div className="flex justify-end pt-2">
                  <Button
                    onClick={() => setResolveSummary(null)}
                    variant="outline"
                    className="text-slate-700"
                    data-testid="resolve-summary-close"
                  >
                    Done
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Auto-applied audit modal — opens when the reviewer clicks
              a link in the "Auto-applied by AI" strip so they can see
              exactly which records were finalized without human clicks. */}
          {autoAppliedAudit && (
            <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
                 data-testid="auto-applied-audit-modal"
                 onClick={() => setAutoAppliedAudit(null)}>
              <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full p-5 space-y-3 max-h-[85vh] overflow-hidden flex flex-col"
                   onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-bold text-slate-900">
                      Auto-applied by AI ·{' '}
                      {autoAppliedAudit.outcome === 'keep' && <span className="text-emerald-700">Kept as Program</span>}
                      {autoAppliedAudit.outcome === 'split' && <span className="text-sky-700">Split into assets</span>}
                      {autoAppliedAudit.outcome === 'exclude' && <span className="text-rose-700">Excluded (deleted)</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {autoAppliedAudit.total.toLocaleString()} record(s) finalized by the AI — showing first {autoAppliedAudit.samples.length}
                      {autoAppliedAudit.total > autoAppliedAudit.samples.length && <> of {autoAppliedAudit.total}</>}.
                      Confidence ≥ {autoAppliedAudit.outcome === 'keep' ? '0.80' : autoAppliedAudit.outcome === 'split' ? '0.90' : '0.95'} threshold auto-applied.
                    </div>
                  </div>
                  <button onClick={() => setAutoAppliedAudit(null)} className="text-slate-400 hover:text-slate-700 text-lg leading-none" data-testid="auto-applied-audit-close">✕</button>
                </div>
                {autoAppliedAudit.samples.length === 0 ? (
                  <div className="text-sm text-slate-500 py-6 text-center">No per-record samples were retained for this run.</div>
                ) : (
                  <div className="overflow-auto flex-1 border border-slate-200 rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr className="text-left text-[10px] uppercase text-slate-500 tracking-wide">
                          <th className="p-2">Source name</th>
                          <th className="p-2">{autoAppliedAudit.outcome === 'keep' ? 'Renamed to' : autoAppliedAudit.outcome === 'split' ? 'Split into' : 'Reason'}</th>
                          <th className="p-2 text-right w-20">Conf.</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {autoAppliedAudit.samples.map((s, i) => (
                          <tr key={i}>
                            <td className="p-2 text-slate-700 max-w-md truncate" title={s.source_name}>{s.source_name || s.asset_id || '—'}</td>
                            <td className="p-2 max-w-md">
                              {autoAppliedAudit.outcome === 'keep' && (
                                <span className="text-emerald-700 font-medium truncate block" title={s.standardized_name}>→ {s.standardized_name || '—'}</span>
                              )}
                              {autoAppliedAudit.outcome === 'split' && (
                                <span className="text-sky-700 truncate block" title={(s.split_assets || []).map(x => x.asset_name).join(' · ')}>
                                  {(s.split_assets || []).map(x => x.asset_name).filter(Boolean).join(' · ')}
                                </span>
                              )}
                              {autoAppliedAudit.outcome === 'exclude' && (
                                <span className="text-rose-700 truncate block" title={s.reason}>{s.reason || '—'}</span>
                              )}
                              {s.reason && autoAppliedAudit.outcome !== 'exclude' && (
                                <span className="text-[10px] text-slate-500 truncate block mt-0.5" title={s.reason}>{s.reason}</span>
                              )}
                            </td>
                            <td className="p-2 text-right font-mono text-slate-500">{Number(s.confidence || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="flex justify-end pt-1">
                  <Button variant="outline" onClick={() => setAutoAppliedAudit(null)} data-testid="auto-applied-audit-done">Done</Button>
                </div>
              </div>
            </div>
          )}


          <div className="text-sm font-semibold text-slate-800">Or run engines individually</div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => downloadAggregate('auto_applied')}
              disabled={downloadingAll === 'auto_applied' || !aggregate.auto_applied}
              variant="outline"
              className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
              data-testid="action-download-master"
            >
              {downloadingAll === 'auto_applied' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              Download Standardized Master ({(aggregate.auto_applied || 0).toLocaleString()})
            </Button>
            <Button
              onClick={() => downloadAggregate('flagged_review')}
              disabled={downloadingAll === 'flagged_review' || !aggregate.flagged_review}
              variant="outline"
              className="text-amber-700 border-amber-300 hover:bg-amber-50"
              data-testid="action-download-flagged"
            >
              {downloadingAll === 'flagged_review' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              Download Flagged for Review ({(aggregate.flagged_review || 0).toLocaleString()})
            </Button>
            {(aggregate.flagged_review || 0) > 0 && (
              <Button
                onClick={startTrgJob}
                disabled={trgStarting || (trgJob && trgJob.status === 'running')}
                variant="outline"
                className="text-amber-800 border-amber-500 hover:bg-amber-50"
                data-testid="action-ai-triage-flagged"
                title="Run Claude Sonnet 4.5 triage on all flagged-for-review records. Nothing is applied automatically."
              >
                {trgStarting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                AI Triage Flagged for Review
              </Button>
            )}
            <Button
              onClick={() => downloadAggregate('portfolio_flagged')}
              disabled={downloadingAll === 'portfolio_flagged' || !aggregate.portfolio_flagged}
              variant="outline"
              className="text-rose-700 border-rose-300 hover:bg-rose-50"
              data-testid="action-download-placeholders"
            >
              {downloadingAll === 'portfolio_flagged' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              Download Portfolio Placeholders ({(aggregate.portfolio_flagged || 0).toLocaleString()})
            </Button>
            {(aggregate.portfolio_flagged || 0) > 0 && (
              <Button
                onClick={startPlcJob}
                disabled={plcStarting || (plcJob && plcJob.status === 'running')}
                variant="outline"
                className="text-violet-700 border-violet-400 hover:bg-violet-50"
                data-testid="action-ai-classify"
                title="Run Claude Sonnet 4.5 second-pass classifier on all portfolio placeholders. Nothing is applied automatically — review + Apply per bucket."
              >
                {plcStarting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                AI Classify Placeholders
              </Button>
            )}
          </div>
          {(aggregate.portfolio_flagged || 0) === 0 && alreadyDeleted && (
            <div className="text-xs text-slate-500 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              All portfolio placeholders have been deleted.
            </div>
          )}
        </div>
      )}


      {/* ═════════════════════════════════════════════════════════════════
          AI PLACEHOLDER CLASSIFIER — job status + apply-per-outcome
          Only shows when a job exists (either running or completed).
          ═════════════════════════════════════════════════════════════════ */}
      {plcJob && (
        <div className="rounded-xl border border-violet-300 bg-gradient-to-br from-violet-50/60 to-white p-5 space-y-3" data-testid="plc-job-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-violet-700 font-bold">AI second-pass classifier</div>
              <div className="text-sm font-semibold text-slate-800 font-mono">{plcJob.job_id}</div>
              <div className="text-xs text-slate-500">
                Started {(plcJob.started_at || '').slice(0, 19).replace('T', ' ')} · {plcJob.counters?.scanned || 0} / {plcJob.total || 0} classified
              </div>
            </div>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${STATUS_COLOR[plcJob.status] || STATUS_COLOR.queued}`}>
              {plcJob.status}
            </span>
            {['queued', 'running'].includes(plcJob.status) && (
              <Button
                onClick={cancelPlcJob}
                variant="outline"
                size="sm"
                className="h-7 text-xs text-amber-800 border-amber-400"
                data-testid="plc-cancel-btn"
                title="Stop this classifier job. Classifications already saved will be preserved."
              >
                Cancel & stop token spend
              </Button>
            )}
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-violet-500 transition-all" style={{
              width: `${plcJob.total ? Math.round(100 * (plcJob.counters?.scanned || 0) / plcJob.total) : 0}%`,
            }} />
          </div>
          {(() => {
            // Auto-apply summary strip — surfaces the "AI executed
            // high-confidence decisions on its own" savings so the
            // reviewer only sees what still needs a human.
            const c = plcJob.counters || {};
            const autoKeep = c.auto_applied_keep || 0;
            const autoSplit = c.auto_applied_split || 0;
            const autoExcl = c.auto_applied_exclude || 0;
            const autoTotal = autoKeep + autoSplit + autoExcl;
            if (!autoTotal) return null;
            const openAudit = (outcome) => {
              const samples = (plcJob.buckets?.[outcome] || []).filter((r) => r.auto_applied);
              setAutoAppliedAudit({
                outcome,
                total: outcome === 'keep' ? autoKeep : outcome === 'split' ? autoSplit : autoExcl,
                samples,
              });
            };
            return (
              <div className="rounded border border-emerald-200 bg-emerald-50/50 px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]" data-testid="plc-auto-applied-strip">
                <span className="font-semibold text-emerald-800 uppercase tracking-wide">Auto-applied by AI</span>
                <span className="text-emerald-800"><strong>{autoTotal}</strong> total</span>
                {autoKeep > 0 && (
                  <button onClick={() => openAudit('keep')} className="text-emerald-700 hover:underline cursor-pointer" data-testid="auto-applied-keep-link" title="Show records auto-kept as Program">
                    <strong>{autoKeep}</strong> kept as Program <span className="text-slate-400">(≥ 0.80)</span>
                  </button>
                )}
                {autoSplit > 0 && (
                  <button onClick={() => openAudit('split')} className="text-sky-700 hover:underline cursor-pointer" data-testid="auto-applied-split-link" title="Show records auto-split into new assets">
                    <strong>{autoSplit}</strong> split <span className="text-slate-400">(≥ 0.90)</span>
                  </button>
                )}
                {autoExcl > 0 && (
                  <button onClick={() => openAudit('exclude')} className="text-rose-700 hover:underline cursor-pointer" data-testid="auto-applied-exclude-link" title="Show records auto-excluded (deleted)">
                    <strong>{autoExcl}</strong> excluded <span className="text-slate-400">(≥ 0.95)</span>
                  </button>
                )}
                <span className="text-slate-500 ml-auto">Everything below these thresholds lands in the buckets for your review.</span>
              </div>
            );
          })()}
          {/* Tabs — driven by LIVE bucket counts (not stale job snapshot).
              Numbers refresh after every Apply so users can see the state
              of the DB change in real time. */}
          {(() => {
            const totals = plcCounts?.totals || {};
            const eligibleMap = plcCounts?.eligible || {};
            const skipMap = plcCounts?.skip_reasons || {};
            const tabs = [
              { key: 'keep', label: 'Keep as Program', tone: 'emerald' },
              { key: 'split', label: 'Split into assets', tone: 'sky' },
              { key: 'exclude', label: 'Exclude (platform)', tone: 'rose' },
              { key: 'error', label: 'Errors', tone: 'slate' },
            ];
            const toneCls = {
              emerald: {
                active: 'border-emerald-500 ring-2 ring-emerald-300 bg-emerald-50',
                idle: 'border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50',
                num: 'text-emerald-800', label: 'text-emerald-700',
                elig: 'text-emerald-700', dl: 'text-emerald-700 hover:bg-emerald-100',
              },
              sky: {
                active: 'border-sky-500 ring-2 ring-sky-300 bg-sky-50',
                idle: 'border-sky-200 bg-sky-50/40 hover:bg-sky-50',
                num: 'text-sky-800', label: 'text-sky-700',
                elig: 'text-sky-700', dl: 'text-sky-700 hover:bg-sky-100',
              },
              rose: {
                active: 'border-rose-500 ring-2 ring-rose-300 bg-rose-50',
                idle: 'border-rose-200 bg-rose-50/40 hover:bg-rose-50',
                num: 'text-rose-800', label: 'text-rose-700',
                elig: 'text-rose-700', dl: 'text-rose-700 hover:bg-rose-100',
              },
              slate: {
                active: 'border-slate-500 ring-2 ring-slate-300 bg-slate-100',
                idle: 'border-slate-200 bg-slate-50/40 hover:bg-slate-50',
                num: 'text-slate-800', label: 'text-slate-600',
                elig: 'text-slate-600', dl: 'text-slate-600 hover:bg-slate-200',
              },
            };
            return (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" role="tablist">
                {tabs.map(({ key, label, tone }) => {
                  const t = toneCls[tone];
                  const active = plcActiveBucket === key;
                  const total = totals[key] || 0;
                  const elig = eligibleMap[key] || 0;
                  const reasons = skipMap[key] || {};
                  const belowConf = reasons.below_confidence || 0;
                  const locked = reasons.committee_locked || 0;
                  const noSplit = reasons.no_split_assets || 0;
                  const missing = reasons.missing_name || 0;
                  return (
                    <div
                      key={key}
                      role="tab"
                      aria-selected={active}
                      className={`relative rounded-lg border p-3 transition cursor-pointer ${active ? t.active : t.idle}`}
                      onClick={() => selectPlcBucket(key)}
                      data-testid={`plc-tile-${key}`}
                    >
                      {/* Download button — only when bucket has records */}
                      {total > 0 && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); downloadPlcBucket(key); }}
                          disabled={plcDownloading === key}
                          className={`absolute top-2 right-2 p-1 rounded ${t.dl} disabled:opacity-50`}
                          title={`Download all ${total} ${label} record(s) as Excel`}
                          data-testid={`plc-download-${key}`}
                        >
                          {plcDownloading === key
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Download className="w-3.5 h-3.5" />}
                        </button>
                      )}
                      <div className={`text-2xl font-bold ${t.num}`}>{total}</div>
                      <div className={`text-[10px] uppercase tracking-wide ${t.label}`}>{label}</div>
                      {key !== 'error' ? (
                        <div className="mt-1.5 space-y-0.5">
                          <div className={`text-[10px] font-semibold ${t.elig}`}>
                            {elig > 0
                              ? <><span className="text-slate-500">Ready to apply · </span>{elig}</>
                              : total === 0
                                ? <span className="text-slate-500">No records classified yet</span>
                                : <span className="text-slate-500">0 ready — all need review</span>}
                          </div>
                          {(belowConf + locked + noSplit + missing) > 0 && (
                            <div className="text-[10px] text-slate-500 leading-snug">
                              {belowConf > 0 && <span title="Confidence below 70%">{belowConf} below 70% confidence</span>}
                              {belowConf > 0 && locked > 0 && <span> · </span>}
                              {locked > 0 && <span title="Under Review / Approved / Rejected on Viatris">{locked} committee-locked</span>}
                              {(belowConf > 0 || locked > 0) && noSplit > 0 && <span> · </span>}
                              {noSplit > 0 && <span title="Claude could not split into ≥2 assets">{noSplit} no split candidates</span>}
                              {(belowConf > 0 || locked > 0 || noSplit > 0) && missing > 0 && <span> · </span>}
                              {missing > 0 && <span title="Missing standardized name from Claude">{missing} missing name</span>}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-[10px] mt-1.5 text-slate-500">
                          {total === 0 ? 'No classification errors' : 'Retry via re-classify'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
          <div className="text-[11px] text-slate-500 -mt-1">
            Click any category to see the underlying records. Review the AI&apos;s proposals, adjust the selection, then Apply.
            Numbers refresh automatically after every action.
          </div>

          {/* Interactive review panel for the active bucket. */}
          {plcActiveBucket && (
            <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3" data-testid="plc-inspector">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs font-semibold text-slate-700">
                  {plcActiveBucket === 'keep' && 'Records Claude wants to KEEP as Programs'}
                  {plcActiveBucket === 'split' && 'Records Claude wants to SPLIT into new assets'}
                  {plcActiveBucket === 'exclude' && 'Records Claude wants to EXCLUDE (platforms)'}
                  {plcActiveBucket === 'error' && 'Records that hit an error during classification'}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => rerunBucketAI(plcActiveBucket)}
                    className="text-[11px] text-violet-700 hover:underline disabled:opacity-50 flex items-center gap-1"
                    disabled={plcRerunning || plcBucketLoading || !plcBucketData?.eligible_count && !plcBucketData?.skipped_count}
                    data-testid="plc-rerun-bucket"
                    title="Re-classify every record in this bucket with the latest AI rules before applying destructive actions"
                  >
                    {plcRerunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    Re-run AI review
                  </button>
                  <span className="text-slate-300">·</span>
                  <button
                    onClick={() => loadPlcBucket(plcActiveBucket)}
                    className="text-[11px] text-violet-700 hover:underline"
                    disabled={plcBucketLoading}
                    data-testid="plc-inspector-refresh"
                  >
                    {plcBucketLoading ? 'refreshing…' : 'refresh'}
                  </button>
                  <button
                    onClick={() => { setPlcActiveBucket(null); setPlcBucketData(null); setPlcSelected({}); setPlcSelectedSkipped({}); }}
                    className="text-[11px] text-slate-500 hover:text-slate-900"
                  >close</button>
                </div>
              </div>

              {plcBucketLoading && !plcBucketData ? (
                <div className="text-xs text-slate-500 py-6 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading eligible records…
                </div>
              ) : !plcBucketData ? null : (
                <>
                  {/* Eligible section — checkbox table + Apply button */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="text-[11px] font-semibold text-slate-700">
                        {plcBucketData.eligible_count} eligible for review
                        {plcActiveBucket !== 'error' && (
                          <span className="text-slate-400 font-normal"> · confidence ≥ {Math.round((plcBucketData.threshold || 0.7) * 100)}%</span>
                        )}
                      </div>
                      {plcActiveBucket !== 'error' && plcBucketData.eligible_count > 0 && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Button
                            onClick={() => applyPlcOutcome(plcActiveBucket)}
                            disabled={plcApplying !== null || Object.values(plcSelected).filter(Boolean).length === 0}
                            size="sm"
                            className={
                              plcActiveBucket === 'keep' ? 'h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white'
                              : plcActiveBucket === 'split' ? 'h-8 text-xs bg-sky-600 hover:bg-sky-700 text-white'
                              : 'h-8 text-xs bg-rose-600 hover:bg-rose-700 text-white'
                            }
                            data-testid={`plc-apply-${plcActiveBucket}`}
                            title={`Approve AI's ${plcActiveBucket} recommendation for the selected rows`}
                          >
                            {plcApplying === plcActiveBucket ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : (
                              plcActiveBucket === 'exclude' ? <Trash2 className="w-3.5 h-3.5 mr-1" />
                              : plcActiveBucket === 'split' ? <ClipboardCheck className="w-3.5 h-3.5 mr-1" />
                              : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                            )}
                            Approve {Object.values(plcSelected).filter(Boolean).length} · {plcActiveBucket}
                          </Button>
                          <Button
                            onClick={() => rejectPlcRows('manual_review',
                              Object.keys(plcSelected).filter((k) => plcSelected[k]))}
                            disabled={plcApplying !== null || Object.values(plcSelected).filter(Boolean).length === 0}
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs text-slate-700 border-slate-300 hover:bg-slate-100"
                            data-testid={`plc-reject-${plcActiveBucket}`}
                            title="Move selected rows to Manual Review — AI classification is cleared, record stays as a placeholder for hand-triage"
                          >
                            {plcApplying === 'reject' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ArrowUpDown className="w-3.5 h-3.5 mr-1" />}
                            Move to Manual Review
                          </Button>
                          {plcActiveBucket !== 'exclude' && (
                            <Button
                              onClick={() => rejectPlcRows('delete',
                                Object.keys(plcSelected).filter((k) => plcSelected[k]))}
                              disabled={plcApplying !== null || Object.values(plcSelected).filter(Boolean).length === 0}
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs text-rose-700 border-rose-300 hover:bg-rose-50"
                              data-testid={`plc-delete-${plcActiveBucket}`}
                              title="Permanently delete selected rows and their engagement links"
                            >
                              {plcApplying === 'reject' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
                              Delete
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    {plcBucketData.eligible_count === 0 ? (
                      // Fully-empty bucket: nothing eligible AND nothing
                      // skipped → "Review Complete" celebration state.
                      // Otherwise defer explanation to the Skipped section.
                      plcBucketData.skipped_count === 0 ? (
                        <div className="rounded border border-emerald-200 bg-emerald-50 p-4 text-center space-y-1" data-testid="plc-review-complete">
                          <div className="flex items-center justify-center gap-2 text-emerald-800 font-semibold text-sm">
                            <CheckCircle2 className="w-4 h-4" /> Review Complete
                          </div>
                          <div className="text-[11px] text-emerald-700">
                            No records left in this bucket. Everything has been either applied, moved to Manual Review, or deleted.
                          </div>
                        </div>
                      ) : (
                        <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                          No records eligible to apply right now.
                          All {plcBucketData.skipped_count} record(s) in this bucket are skipped — see the reasons below and use &ldquo;Force approve&rdquo; or &ldquo;Move to Manual Review&rdquo; to finalize.
                        </div>
                      )
                    ) : (
                      <div className="max-h-96 overflow-auto border border-slate-200 rounded">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 sticky top-0 z-10">
                            <tr className="text-left text-[10px] uppercase text-slate-500 tracking-wide">
                              <th className="p-2 w-8">
                                <input
                                  type="checkbox"
                                  checked={plcBucketData.eligible.length > 0
                                    && plcBucketData.eligible.every((r) => plcSelected[r.asset_id])}
                                  onChange={togglePlcAll}
                                  data-testid="plc-select-all"
                                />
                              </th>
                              <th className="p-2">Asset</th>
                              <th className="p-2">Company</th>
                              <th className="p-2">AI proposal</th>
                              <th className="p-2 w-16 text-right">Conf.</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {plcBucketData.eligible.map((row) => (
                              <tr key={row.asset_id} className={plcSelected[row.asset_id] ? 'bg-violet-50/40' : ''}>
                                <td className="p-2 align-top">
                                  <input
                                    type="checkbox"
                                    checked={!!plcSelected[row.asset_id]}
                                    onChange={() => togglePlcRow(row.asset_id)}
                                    data-testid={`plc-row-check-${row.asset_id}`}
                                  />
                                </td>
                                <td className="p-2 align-top">
                                  <div className="font-medium text-slate-800 max-w-md truncate" title={row.asset_name}>
                                    {row.asset_name || row.asset_id}
                                  </div>
                                  {(row.indication || row.moa) && (
                                    <div className="text-[10px] text-slate-500 max-w-md truncate">
                                      {[row.moa, row.indication].filter(Boolean).join(' · ')}
                                    </div>
                                  )}
                                </td>
                                <td className="p-2 align-top text-slate-600 max-w-[10rem] truncate" title={row.company_name}>
                                  {row.company_name || '—'}
                                </td>
                                <td className="p-2 align-top">
                                  {plcActiveBucket === 'keep' && row.standardized_name && (
                                    <div className="text-emerald-700 font-medium truncate max-w-md" title={row.standardized_name}>
                                      → {row.standardized_name}
                                    </div>
                                  )}
                                  {plcActiveBucket === 'split' && Array.isArray(row.split_assets) && row.split_assets.length > 0 && (
                                    <div className="text-sky-700 truncate max-w-md" title={row.split_assets.map(s => s.asset_name).filter(Boolean).join(' · ')}>
                                      → split: {row.split_assets.map(s => s.asset_name).filter(Boolean).join(' · ')}
                                    </div>
                                  )}
                                  {plcActiveBucket === 'exclude' && (
                                    <div className="text-rose-700">→ delete platform record</div>
                                  )}
                                  {row.reason && (
                                    <div className="text-[10px] text-slate-500 max-w-md truncate mt-0.5" title={row.reason}>
                                      {row.reason}
                                    </div>
                                  )}
                                </td>
                                <td className="p-2 align-top text-right font-mono text-slate-600">
                                  {Number(row.confidence || 0).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Skipped section — always visible when there are
                      skipped rows. Below-confidence rows are selectable
                      so the reviewer can override the AI's confidence
                      gate and force-apply after manual inspection.
                      Committee-locked rows and structural blockers
                      remain non-forceable (they're hard blockers, not
                      threshold blockers). */}
                  {plcBucketData.skipped_count > 0 && (
                    <div className="rounded border border-amber-200 bg-amber-50/40 p-3 space-y-2" data-testid="plc-skipped">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-[11px] font-semibold text-amber-900">
                            {plcBucketData.skipped_count} record(s) skipped
                          </div>
                          {(plcBucketData.skip_reason_counts?.below_confidence || 0) > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-amber-200 text-amber-800">
                              {plcBucketData.skip_reason_counts.below_confidence} below confidence
                            </span>
                          )}
                          {(plcBucketData.skip_reason_counts?.committee_locked || 0) > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-amber-200 text-amber-800">
                              {plcBucketData.skip_reason_counts.committee_locked} committee-locked
                            </span>
                          )}
                          {(plcBucketData.skip_reason_counts?.no_split_assets || 0) > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-amber-200 text-amber-800">
                              {plcBucketData.skip_reason_counts.no_split_assets} no split candidates
                            </span>
                          )}
                          {(plcBucketData.skip_reason_counts?.missing_name || 0) > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-amber-200 text-amber-800">
                              {plcBucketData.skip_reason_counts.missing_name} missing name
                            </span>
                          )}
                        </div>
                        {(() => {
                          // Which skipped rows can be force-applied?
                          // Below-confidence (keep/split/exclude) — the AI produced a real
                          // proposal, human is just overriding the threshold.
                          // NOT forceable: committee-locked (data integrity),
                          // no_split_assets (nothing to apply), missing_name (nothing to write).
                          const forceableCount = Object.entries(plcSelectedSkipped)
                            .filter(([, v]) => v).length;
                          const anyForceableAvailable = (plcBucketData.skipped || []).some(
                            (r) => r.skip_reason && r.skip_reason.startsWith('Confidence '),
                          );
                          if (!anyForceableAvailable || plcActiveBucket === 'error') return null;
                          return (
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                onClick={togglePlcSkippedAll}
                                className="text-[11px] text-amber-800 hover:underline"
                                data-testid="plc-skipped-select-all"
                              >
                                {forceableCount > 0 ? 'Deselect all' : 'Select all below-confidence'}
                              </button>
                              <Button
                                onClick={() => forceApplyPlcSkipped(plcActiveBucket)}
                                disabled={plcApplying !== null || forceableCount === 0}
                                size="sm"
                                variant="outline"
                                className="h-7 text-[11px] text-amber-800 border-amber-400 hover:bg-amber-100"
                                data-testid={`plc-force-apply-${plcActiveBucket}`}
                                title="Override the 70% confidence gate and finalize these records after your manual review"
                              >
                                {plcApplying === plcActiveBucket
                                  ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  : <CheckCircle2 className="w-3 h-3 mr-1" />}
                                Force approve {forceableCount || ''}
                              </Button>
                              <Button
                                onClick={() => rejectPlcRows('manual_review',
                                  Object.keys(plcSelectedSkipped).filter((k) => plcSelectedSkipped[k]))}
                                disabled={plcApplying !== null || forceableCount === 0}
                                size="sm"
                                variant="outline"
                                className="h-7 text-[11px] text-slate-700 border-slate-300 hover:bg-slate-100"
                                data-testid={`plc-skipped-reject-${plcActiveBucket}`}
                                title="AI got this wrong — move selected rows to Manual Review"
                              >
                                {plcApplying === 'reject' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ArrowUpDown className="w-3 h-3 mr-1" />}
                                Move to Manual Review
                              </Button>
                              <Button
                                onClick={() => rejectPlcRows('delete',
                                  Object.keys(plcSelectedSkipped).filter((k) => plcSelectedSkipped[k]))}
                                disabled={plcApplying !== null || forceableCount === 0}
                                size="sm"
                                variant="outline"
                                className="h-7 text-[11px] text-rose-700 border-rose-300 hover:bg-rose-50"
                                data-testid={`plc-skipped-delete-${plcActiveBucket}`}
                                title="Permanently delete selected rows"
                              >
                                {plcApplying === 'reject' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                                Delete
                              </Button>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="max-h-56 overflow-auto text-[11px] divide-y divide-amber-100">
                        {plcBucketData.skipped.slice(0, 200).map((row) => {
                          const forceable = row.skip_reason && row.skip_reason.startsWith('Confidence ');
                          return (
                            <div key={row.asset_id} className="py-1.5 flex items-start gap-2">
                              {forceable ? (
                                <input
                                  type="checkbox"
                                  className="mt-0.5"
                                  checked={!!plcSelectedSkipped[row.asset_id]}
                                  onChange={() => togglePlcSkippedRow(row.asset_id)}
                                  data-testid={`plc-skipped-check-${row.asset_id}`}
                                  title="Force-approve this record"
                                />
                              ) : (
                                <span className="w-3 h-3 mt-0.5" />
                              )}
                              <span className="text-slate-700 flex-1 truncate" title={row.asset_name}>
                                {row.asset_name || row.asset_id}
                              </span>
                              <span className="text-amber-800 shrink-0">{row.skip_reason}</span>
                            </div>
                          );
                        })}
                        {plcBucketData.skipped.length > 200 && (
                          <div className="py-1.5 text-[10px] text-slate-400">
                            … plus {plcBucketData.skipped.length - 200} more.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════════
          AI FLAGGED-REVIEW TRIAGE — 5-bucket splitter
          ═════════════════════════════════════════════════════════════════ */}
      {trgJob && (
        <div className="rounded-xl border border-amber-400 bg-gradient-to-br from-amber-50/60 to-white p-5 space-y-3" data-testid="trg-job-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-800 font-bold">AI triage — flagged for review</div>
              <div className="text-sm font-semibold text-slate-800 font-mono">{trgJob.job_id}</div>
              <div className="text-xs text-slate-500">
                Started {(trgJob.started_at || '').slice(0, 19).replace('T', ' ')} · {trgJob.counters?.scanned || 0} / {trgJob.total || 0} classified
              </div>
            </div>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${STATUS_COLOR[trgJob.status] || STATUS_COLOR.queued}`}>
              {trgJob.status}
            </span>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-amber-500 transition-all" style={{
              width: `${trgJob.total ? Math.round(100 * (trgJob.counters?.scanned || 0) / trgJob.total) : 0}%`,
            }} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            {[
              { key: 'system_failure', label: 'System failure', tone: 'slate' },
              { key: 'needs_second_pass', label: 'Needs 2nd pass', tone: 'sky' },
              { key: 'insufficient_info', label: 'Insufficient info', tone: 'slate' },
              { key: 'out_of_scope', label: 'Out of scope', tone: 'rose' },
              { key: 'human_review', label: 'Human review', tone: 'emerald' },
              { key: 'error', label: 'Errors', tone: 'slate' },
            ].map(({ key, label, tone }) => {
              const active = trgActiveBucket === key;
              const toneCls = {
                slate: 'border-slate-200 bg-slate-50 text-slate-800',
                sky: 'border-sky-200 bg-sky-50 text-sky-800',
                rose: 'border-rose-200 bg-rose-50 text-rose-800',
                emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
              }[tone];
              const activeRing = active ? 'ring-2 ring-violet-400 border-violet-400' : '';
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTrgActiveBucket(active ? null : key)}
                  className={`text-left rounded-lg border ${toneCls} ${activeRing} p-3 hover:brightness-95 transition`}
                  data-testid={`trg-tile-${key}`}
                >
                  <div className="text-2xl font-bold">{trgJob.counters?.[key] || 0}</div>
                  <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
                </button>
              );
            })}
          </div>
          <div className="text-[11px] text-slate-500 -mt-1">
            Click any tile to preview the records Claude classified into that bucket.
          </div>

          {/* Inline inspector for triage buckets */}
          {trgActiveBucket && (
            <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2" data-testid="trg-inspector">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-slate-700">
                  {trgActiveBucket.replace(/_/g, ' ')} · showing up to 100 samples
                </div>
                <button
                  onClick={() => setTrgActiveBucket(null)}
                  className="text-[11px] text-slate-500 hover:text-slate-900"
                >close</button>
              </div>
              {(trgJob.buckets?.[trgActiveBucket] || []).length === 0 ? (
                <div className="text-xs text-slate-500 py-3">No samples in this bucket.</div>
              ) : (
                <div className="max-h-80 overflow-auto text-xs divide-y divide-slate-100">
                  {(trgJob.buckets?.[trgActiveBucket] || []).map((row, i) => (
                    <div key={i} className="py-1.5 space-y-0.5">
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-slate-800 truncate flex-1" title={row.source_name}>
                          {row.source_name || row.asset_id || '—'}
                        </span>
                        {row.confidence !== undefined && row.confidence !== null && (
                          <span className="text-[10px] text-slate-500 shrink-0">
                            conf {Number(row.confidence).toFixed(2)}
                          </span>
                        )}
                      </div>
                      {trgActiveBucket === 'needs_second_pass' && row.program_name && (
                        <div className="text-[11px]">
                          <span className="text-slate-400">→ rename to </span>
                          <span className="text-sky-700 font-medium">{row.program_name}</span>
                        </div>
                      )}
                      {row.reason && (
                        <div className="text-[11px] text-slate-500" title={row.reason}>{row.reason}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {trgJob.status === 'done' && (
            <div className="pt-3 border-t border-amber-300/70 space-y-2">
              <div className="text-xs font-semibold text-slate-700">Apply triage decisions (confidence ≥ 70%; system_failure is deterministic)</div>
              {trgAggregate && (
                <div className="text-[11px] text-slate-500 bg-amber-50/60 border border-amber-200 rounded px-2 py-1.5">
                  <span className="font-semibold text-amber-800">Note:</span> counters below show the FULL DB pool
                  (across every triage run), not just this job. Apply runs on the entire pool.
                  {trgAggregate.unclassified > 0 && (
                    <span className="ml-1">
                      {` `}<span className="font-semibold text-slate-700">{trgAggregate.unclassified.toLocaleString()}</span> records still need classification —
                      click <span className="font-semibold">AI Resolve Remaining</span> above to triage them.
                    </span>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => applyTrgBucket('system_failure')}
                  disabled={trgApplying !== null || !((trgAggregate?.buckets?.system_failure?.eligible ?? trgJob.counters?.system_failure) > 0)}
                  variant="outline"
                  className="text-slate-700 border-slate-400 hover:bg-slate-50"
                  data-testid="trg-apply-system-failure"
                >
                  {trgApplying === 'system_failure' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-1" />}
                  Retry {(trgAggregate?.buckets?.system_failure?.eligible ?? trgJob.counters?.system_failure ?? 0).toLocaleString()} system failures
                </Button>
                <Button
                  onClick={() => applyTrgBucket('needs_second_pass')}
                  disabled={trgApplying !== null || !((trgAggregate?.buckets?.needs_second_pass?.eligible ?? trgJob.counters?.needs_second_pass) > 0)}
                  variant="outline"
                  className="text-sky-700 border-sky-300 hover:bg-sky-50"
                  data-testid="trg-apply-second-pass"
                >
                  {trgApplying === 'needs_second_pass' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ClipboardCheck className="w-4 h-4 mr-1" />}
                  Rename {(trgAggregate?.buckets?.needs_second_pass?.eligible ?? trgJob.counters?.needs_second_pass ?? 0).toLocaleString()} to Program names
                </Button>
                <Button
                  onClick={() => applyTrgBucket('insufficient_info')}
                  disabled={trgApplying !== null || !((trgAggregate?.buckets?.insufficient_info?.eligible ?? trgJob.counters?.insufficient_info) > 0)}
                  variant="outline"
                  className="text-slate-700 border-slate-300 hover:bg-slate-50"
                  data-testid="trg-apply-insufficient"
                >
                  {trgApplying === 'insufficient_info' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ClipboardCheck className="w-4 h-4 mr-1" />}
                  Tag {(trgAggregate?.buckets?.insufficient_info?.eligible ?? trgJob.counters?.insufficient_info ?? 0).toLocaleString()} as insufficient info
                </Button>
                <Button
                  onClick={() => applyTrgBucket('out_of_scope')}
                  disabled={trgApplying !== null || !((trgAggregate?.buckets?.out_of_scope?.eligible ?? trgJob.counters?.out_of_scope) > 0)}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="trg-apply-out-of-scope"
                >
                  {trgApplying === 'out_of_scope' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                  Delete {(trgAggregate?.buckets?.out_of_scope?.eligible ?? trgJob.counters?.out_of_scope ?? 0).toLocaleString()} out-of-scope
                </Button>
              </div>
              <div className="text-[11px] text-slate-500">
                Human review records ({(trgAggregate?.buckets?.human_review?.total ?? trgJob.counters?.human_review ?? 0).toLocaleString()}) stay in the queue and are handled manually.
              </div>
            </div>
          )}
        </div>
      )}


      {/* ═════════════════════════════════════════════════════════════════
          START A NEW RUN
          ═════════════════════════════════════════════════════════════════ */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="run-controls">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <div className="text-sm font-semibold text-slate-800">Start a standardization run</div>
            <div className="text-xs text-slate-500">
              Scans only assets not yet reviewed by Claude ({(aggregate?.not_yet_scanned || 0).toLocaleString()} remaining). Cascades to Committee Review, Reviewer, Client Portal.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => startRun(true)}
              disabled={starting || !runnable}
              variant="outline"
              className="text-slate-700 border-slate-300"
              data-testid="start-dry-run-btn"
              title="Preview what would change without writing to master."
            >
              {starting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-1" />}
              Start Dry-Run
            </Button>
            <Button
              onClick={() => startRun(false)}
              disabled={starting || !runnable}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="start-auto-apply-btn"
              title="Run and immediately apply changes to the master."
            >
              {starting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-1" />}
              Start &amp; Auto-Apply
            </Button>
          </div>
        </div>
      </div>

      {/* ═════════════════════════════════════════════════════════════════
          RUN HISTORY / AUDIT TRAIL (collapsible)
          ═════════════════════════════════════════════════════════════════ */}
      {jobs.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid="job-history">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-between text-left"
            data-testid="history-toggle"
          >
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Audit trail</div>
              <div className="text-sm font-semibold text-slate-800">Run history · {jobs.length} run{jobs.length === 1 ? '' : 's'}</div>
            </div>
            <span className="text-xs text-slate-500">{showHistory ? 'Hide' : 'Show'}</span>
          </button>
          {showHistory && (
            <div className="space-y-1 pt-2 border-t border-slate-100">
              {jobs.map((j) => (
                <button
                  key={j.job_id}
                  onClick={() => setActiveJobId(j.job_id)}
                  className={`w-full text-left flex items-center justify-between px-3 py-1.5 rounded border ${
                    j.job_id === activeJobId ? 'border-violet-400 bg-violet-50' : 'border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid={`job-history-row-${j.job_id}`}
                >
                  <div>
                    <div className="text-xs font-mono text-slate-700">{j.job_id}</div>
                    <div className="text-[10px] text-slate-500">
                      {(j.started_at || '').slice(0, 19).replace('T', ' ')} · scanned {j.counters?.scanned || 0} / {j.total || 0}
                      {j.dry_run && <span className="ml-1 text-amber-700">· dry-run</span>}
                      {j.status === 'failed' && j.error && <span className="ml-1 text-rose-700 truncate">· {String(j.error).slice(0, 80)}</span>}
                    </div>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${STATUS_COLOR[j.status] || STATUS_COLOR.queued}`}>
                    {j.status}
                  </span>
                </button>
              ))}
              {activeJob && (
                <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-500">
                  <strong>Selected run details:</strong>{' '}
                  {(counters.auto_applied || 0)} auto-applied · {(counters.portfolio_flagged || 0)} portfolio ·{' '}
                  {(counters.flagged_review || 0)} flagged · {(counters.unchanged || 0)} unchanged · {(counters.errors || 0)} errors.
                  {stuckLikely && (
                    <span className="ml-2">
                      <Button onClick={cancelJob} variant="outline" size="sm" className="h-6 text-[10px] text-amber-800 border-amber-400" data-testid="cancel-stuck-job-btn">
                        Cancel stuck job
                      </Button>
                    </span>
                  )}
                  {activeJob.dry_run && activeJob.status === 'done' && (counters.auto_applied || 0) > 0 && (
                    <span className="ml-2">
                      <Button onClick={applyDryRun} size="sm" className="h-6 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="apply-dry-run-btn">
                        <ClipboardCheck className="w-3 h-3 mr-1" /> Apply {counters.auto_applied}
                      </Button>
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Counter({ label, value, tone, highlight = false, active = false, onClick, onDownload, downloading = false, testid }) {
  const cls = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100',
    amber: 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100',
    rose: 'bg-rose-50 border-rose-200 text-rose-800 hover:bg-rose-100',
    slate: 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100',
  }[tone] || 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100';
  const activeRing = active ? 'ring-2 ring-violet-500 ring-offset-1' : '';
  const highlightRing = highlight && !active ? 'ring-2 ring-rose-400 animate-pulse' : '';
  const hasRows = (value || 0) > 0;
  const Inner = (
    <>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-wide opacity-80 flex items-center gap-1">
        {label}
        {active && <span className="text-[9px] font-semibold px-1 rounded bg-violet-100 text-violet-700 border border-violet-200">viewing</span>}
      </div>
    </>
  );
  return (
    <div className={`relative rounded-lg border ${cls} ${activeRing} ${highlightRing} p-3 transition w-full`}>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          data-testid={testid}
          className="text-left w-full cursor-pointer"
          title="Click to inspect records in this bucket"
        >
          {Inner}
        </button>
      ) : (
        <div data-testid={testid} className="text-left w-full">
          {Inner}
        </div>
      )}
      {onDownload && hasRows && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDownload(); }}
          disabled={downloading}
          data-testid={`${testid}-download-btn`}
          className="absolute top-2 right-2 rounded p-1 text-slate-500 hover:text-slate-900 hover:bg-white/70 transition disabled:opacity-40"
          title="Download this bucket as Excel (.xlsx)"
        >
          {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
        </button>
      )}
    </div>
  );
}

const BUCKET_META = {
  auto_applied: { title: 'Auto-applied', tone: 'emerald', testid: 'bucket-auto-applied', showDiff: true },
  portfolio_flagged: { title: 'Portfolio placeholders', tone: 'rose', testid: 'bucket-portfolio-flagged', showReason: true },
  flagged_review: { title: 'Flagged for review', tone: 'amber', testid: 'bucket-flagged-review', showReason: true },
  unchanged: { title: 'Unchanged', tone: 'slate', testid: 'bucket-unchanged' },
  errors: { title: 'Errors', tone: 'rose', testid: 'bucket-errors', showError: true },
};

function BucketInspector({ bucketKey, items, total, showSafetyBanner }) {
  const meta = BUCKET_META[bucketKey] || {};
  const border = meta.tone === 'rose' ? 'border-rose-200'
    : meta.tone === 'emerald' ? 'border-emerald-200'
    : meta.tone === 'amber' ? 'border-amber-200'
    : 'border-slate-200';
  const shown = items.length;
  return (
    <div className="space-y-2" data-testid={meta.testid}>
      {showSafetyBanner && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900" data-testid="portfolio-safety-banner">
          <strong>Nothing has been deleted.</strong> These records are only <em>flagged</em> for your review.
          Deletion only happens after you click the red <em>Bulk-Delete</em> button and confirm the popup.
          Assets with committee status Under Review / Approved / Rejected are auto-skipped as a safety net.
        </div>
      )}
      <div className={`rounded-lg border ${border} p-3 space-y-2`}>
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold text-slate-700">
            {meta.title} · {total} total
          </div>
          {shown < total && (
            <div className="text-[10px] text-slate-500">
              showing first {shown} · sample only
            </div>
          )}
        </div>
        {shown === 0 ? (
          <div className="text-xs text-slate-500 py-2">No records in this bucket.</div>
        ) : (
          <div className="max-h-96 overflow-auto text-xs divide-y divide-slate-100">
            {items.map((e, i) => (
              <BucketRow
                key={i}
                entry={e}
                showDiff={meta.showDiff}
                showReason={meta.showReason}
                showError={meta.showError}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BucketRow({ entry, showDiff, showReason, showError }) {
  const diff = entry.diff || {};
  const diffKeys = Object.keys(diff);
  return (
    <div className="py-2 space-y-1">
      <div className="flex items-start gap-2">
        <span className="font-mono text-slate-700 truncate flex-1" title={entry.source_name || entry.asset_id}>
          {entry.source_name || entry.asset_id || '—'}
        </span>
        {entry.std?.confidence !== undefined && (
          <span className="text-[10px] text-slate-500 shrink-0">
            conf {Number(entry.std.confidence).toFixed(2)}
          </span>
        )}
      </div>
      {showDiff && diffKeys.length > 0 && (
        <div className="text-[11px] space-y-0.5 pl-2 border-l-2 border-emerald-200">
          {diffKeys.map((k) => (
            <div key={k} className="flex gap-2">
              <span className="text-slate-500 w-24 shrink-0">{k}:</span>
              <span className="text-rose-700 line-through truncate max-w-[35%]" title={String(diff[k]?.from ?? '')}>
                {String(diff[k]?.from ?? '—')}
              </span>
              <span className="text-slate-400">→</span>
              <span className="text-emerald-700 truncate flex-1" title={String(diff[k]?.to ?? '')}>
                {String(diff[k]?.to ?? '—')}
              </span>
            </div>
          ))}
        </div>
      )}
      {showReason && entry.std?.reason && (
        <div className="text-[11px] text-slate-600 pl-2 border-l-2 border-amber-200" title={entry.std.reason}>
          {entry.std.reason}
        </div>
      )}
      {showError && entry.error && (
        <div className="text-[11px] text-rose-700 pl-2 border-l-2 border-rose-200" title={entry.error}>
          {entry.error}
        </div>
      )}
    </div>
  );
}
